/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { Drawer, message, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../Core/Components/CustomButton";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import { PopoverDropdown } from "../../../Core/Components/CustomDropdown";
import { LOCATION_TYPE } from "../../Location/Data";
import { formatDateTimeWithToday } from "../../../Utils/formatDate";
// import { updateTrips } from "../../../Redux/Slices/trips.slice";
import {
  getTrikeBreakdownByTrip,
  getHubAgents,
  assignTrikeBreakdownAgent,
  rejectTrikeBreakdown,
} from "../../../Redux/APIs/trikeBreakdownAPI";

export const trikeRepairSteps = {
  REQUEST_REJECTED: 0,
  REQUEST_CREATED: 1,
  SELECT_AGENT: 2,
  REQUEST_ACCEPTED: 3,
  REQUEST_COMPLETED: 4,
  REPLACEMENT_REQUESTED: 5,
};

const statusToStep = {
  1: trikeRepairSteps.REQUEST_CREATED, // PENDING
  2: trikeRepairSteps.REQUEST_ACCEPTED, // ACCEPTED
  3: trikeRepairSteps.REQUEST_REJECTED, // REJECTED
  4: trikeRepairSteps.REQUEST_ACCEPTED, // IN_PROGRESS
  5: trikeRepairSteps.REQUEST_COMPLETED, // COMPLETED
  6: trikeRepairSteps.REPLACEMENT_REQUESTED, // REPLACEMENT_REQUESTED
};

const TrikeRepairDrawer = ({
  visible,
  onCancel,
  isRental = false,
  tripId,
  rentalId,
}) => {
  const dispatch = useDispatch();
  const { activeBreakdownRequest, currentTrip } = useSelector(
    (state) => state.trips,
  );
  const { currentRental } = useSelector((state) => state.rentals);
  const activeId = isRental
    ? rentalId || currentRental?.id
    : tripId || currentTrip?.id;

  const [localStep, setLocalStep] = useState(null);
  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentsList, setAgentsList] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Sync step from backend status unless user is navigating locally
  let step = trikeRepairSteps.REQUEST_CREATED;

  if (localStep !== null) {
    step = localStep;
  } else if (activeBreakdownRequest) {
    step =
      statusToStep[activeBreakdownRequest.status] ??
      trikeRepairSteps.REQUEST_CREATED;
  }

  const { allLocations } = useSelector((state) => state.locations);

  console.log({ activeBreakdownRequest });

  useEffect(() => {
    if (visible) {
      const targetHubId = Number(
        activeBreakdownRequest?.hubId ||
          activeBreakdownRequest?.hub_id ||
          (isRental
            ? currentRental?.hubId ||
              currentRental?.hub_id ||
              currentRental?.original_batt_hub
            : currentTrip?.hubId || currentTrip?.hub_id) ||
          0,
      );

      let hubObj = null;
      if (targetHubId) {
        const found = allLocations.find(
          (loc) =>
            loc.type === LOCATION_TYPE.HUB_STATION &&
            Number(loc.id) === targetHubId,
        );
        if (found) {
          hubObj = { id: found.id, value: found.name };
        } else {
          hubObj = { id: targetHubId, value: "Hub Station" };
        }
      }

      if (hubObj) {
        setSelectedHub(hubObj);
        dispatch(getHubAgents({ hubId: hubObj.id }))
          .unwrap()
          .then((agents) => setAgentsList(agents || []))
          .catch((err) => console.error("Failed to load hub agents", err));
      }
    } else {
      setLocalStep(null);
      setSelectedHub(null);
      setSelectedAgent(null);
      setAgentsList([]);
    }
  }, [
    visible,
    activeBreakdownRequest,
    currentTrip,
    currentRental,
    allLocations,
    dispatch,
    isRental,
  ]);

  const handleSelectHub = (hub) => {
    setSelectedHub(hub);
    setSelectedAgent(null);
    setActionLoading(true);
    dispatch(getHubAgents({ hubId: hub.id }))
      .unwrap()
      .then((agents) => {
        setAgentsList(agents || []);
      })
      .catch(() => {
        message.error("Failed to load hub agents");
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const handleAssignAndAccept = () => {
    if (!activeBreakdownRequest?.id || !selectedHub || !selectedAgent) return;
    setActionLoading(true);
    dispatch(
      assignTrikeBreakdownAgent({
        id: activeBreakdownRequest.id,
        hubId: selectedHub.id,
        agentId: selectedAgent.id,
      }),
    )
      .unwrap()
      .then(() => {
        message.success("Agent assigned successfully");
        if (activeId) {
          dispatch(
            getTrikeBreakdownByTrip(
              isRental ? { rentalId: activeId } : { tripId: activeId },
            ),
          );
        }
        setLocalStep(null);
        setSelectedHub(null);
        setSelectedAgent(null);
        setAgentsList([]);
      })
      .catch(() => {
        message.error("Failed to assign agent");
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const handleReject = () => {
    if (!activeBreakdownRequest?.id) return;
    setActionLoading(true);
    dispatch(rejectTrikeBreakdown({ id: activeBreakdownRequest.id }))
      .unwrap()
      .then(() => {
        message.success("Request rejected successfully");
        if (activeId) {
          dispatch(
            getTrikeBreakdownByTrip(
              isRental ? { rentalId: activeId } : { tripId: activeId },
            ),
          );
        }
        onCancel();
      })

      .catch(() => {
        message.error("Failed to reject request");
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const closeButtonWidth = (() => {
    if (step === trikeRepairSteps.REQUEST_CREATED) return "batterySwap";
    if (
      step === trikeRepairSteps.REQUEST_ACCEPTED ||
      step === trikeRepairSteps.REQUEST_COMPLETED ||
      step === trikeRepairSteps.REPLACEMENT_REQUESTED
    )
      return "full";
    return "batterySwapStep3";
  })();

  const assignmentSuccessSteps = [
    trikeRepairSteps.REQUEST_ACCEPTED,
    trikeRepairSteps.REPLACEMENT_REQUESTED,
    trikeRepairSteps.REQUEST_COMPLETED,
  ];

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      open={visible}
      onClose={onCancel}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6">
          <p className="font-poppins font-medium text-base leading-none ">
            Request Details
          </p>

          <CloseIcon
            className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
            onClick={onCancel}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-0">
          <Spin spinning={actionLoading} className="h-screen">
            {step === trikeRepairSteps.REQUEST_CREATED && (
              <RequestDetails data={activeBreakdownRequest} />
            )}
            {step === trikeRepairSteps.SELECT_AGENT && (
              <SelectAgent
                selectedHub={selectedHub}
                selectedAgent={selectedAgent}
                agentsList={agentsList}
                onSelectHub={handleSelectHub}
                onSelectAgent={setSelectedAgent}
                updateStep={setLocalStep}
              />
            )}
            {activeBreakdownRequest &&
              assignmentSuccessSteps.includes(step) && (
                <AssignmentSuccess step={step} data={activeBreakdownRequest} />
              )}
          </Spin>
        </div>

        <div className="p-6 flex justify-between gap-4">
          <Button
            onClick={onCancel}
            width={closeButtonWidth}
            colors="batterySwapClose"
          >
            Close
          </Button>

          {step === trikeRepairSteps.REQUEST_CREATED && (
            <>
              <Button colors="alert" width="batterySwap" onClick={handleReject}>
                Reject
              </Button>
              <Button
                type="primary"
                width="batterySwap"
                onClick={() => setLocalStep(trikeRepairSteps.SELECT_AGENT)}
              >
                Accept
              </Button>
            </>
          )}

          {step === trikeRepairSteps.SELECT_AGENT && (
            <Button
              type="primary"
              width="batterySwapStep3"
              onClick={handleAssignAndAccept}
            >
              Assign & Accept
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
};

const RequestDetails = ({ data }) => {
  if (!data) return null;

  const formattedDate = formatDateTimeWithToday(
    data.created_at || data.createdAt,
  );

  const reasonsText = data.reasons;

  const photosArray =
    typeof data.photos === "string" ? JSON.parse(data.photos) : data.photos;

  return (
    <div
      className="
        rounded-xl
        border
        border-yellow
        bg-yellow-10
        px-6 py-4
      "
    >
      <div className="flex justify-between items-start">
        <div className="bg-yellow px-2 py-1 rounded-md">
          <span className="font-poppins leading-none text-xs font-medium text-black">
            Trike Repair
          </span>
        </div>

        <p className="font-poppins font-normal text-xs text-dark-gray leading-none mt-1">
          {formattedDate}
        </p>
      </div>

      <div className="mt-4 flex items-center gap-x-4">
        <LabelAndValue label="Driver" value={data.driverName} />
        <LabelAndValue label="Contact No." value={data.driverContact || "--"} />
      </div>
      <div className="mt-4 flex items-center gap-x-4">
        {data.tripId ? (
          <LabelAndValue label="Trip ID" value={data.tripId || "--"} />
        ) : (
          data.rentalId && (
            <LabelAndValue label="Rental ID" value={data.rentalId || "--"} />
          )
        )}
        <LabelAndValue
          label="Driver Location"
          value={data.driverLocation || "--"}
        />
      </div>

      <div className="mt-4 flex items-center gap-x-4">
        <LabelAndValue label="Reasons" value={reasonsText} />
      </div>

      {photosArray && photosArray.length > 0 && (
        <div className="flex flex-col mt-4">
          <div className="flex gap-2 flex-wrap">
            {photosArray.map((url, idx) => (
              <a href={url} target="_blank" rel="noopener noreferrer" key={url}>
                <img
                  src={url}
                  alt={`evidence-${idx}`}
                  className="w-16 h-16 object-cover rounded-md border border-light-grey cursor-pointer hover:opacity-80"
                />
              </a>
            ))}
          </div>
          <p className="font-poppins font-normal text-xs text-dark-gray mt-2">
            Photos
          </p>
        </div>
      )}
    </div>
  );
};

const SelectAgent = ({
  selectedHub,
  selectedAgent,
  agentsList,
  onSelectHub,
  onSelectAgent,
  updateStep,
}) => {
  const { allLocations } = useSelector((state) => state.locations);

  const hubStations = allLocations
    .filter((item) => item.type === LOCATION_TYPE.HUB_STATION)
    .map((location) => ({
      id: location.id,
      value: location.name,
    }));

  const agents = agentsList.map((a) => ({
    id: a.id,
    value: a.name,
  }));

  return (
    <>
      <div className="flex items-center gap-3 mb-6 border-t border-t-light-grey pt-6">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className=" cursor-pointer"
          onClick={() => updateStep(trikeRepairSteps.REQUEST_CREATED)}
        >
          <path
            d="M12.5005 16.6004L7.06719 11.1671C6.42552 10.5254 6.42552 9.47539 7.06719 8.83372L12.5005 3.40039"
            stroke="#525252"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <p className="font-poppins font-medium text-sm leading-none">
          Pickup at Hub Station
        </p>
      </div>
      <div className="flex justify-between gap-4">
        <div className="flex-1">
          <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
            Hub Station
          </p>
          <PopoverDropdown
            disabled
            enableSearch
            searchPlaceholder="Search Hub Station"
            placeholder="Assign Hub Station"
            className="max-h-[300px] overflow-y-scroll"
            valueStyle={{ width: "220px" }}
            options={hubStations}
            value={selectedHub?.value}
            onSelect={onSelectHub}
          />
        </div>
        <div className="flex-1">
          <p className="font-aileron font-normal text-xs leading-none text-dark-gray mb-2">
            Technical Agent
          </p>
          <PopoverDropdown
            enableSearch
            placeholder="Select Technical Agent"
            searchPlaceholder="Search Agent"
            className="max-h-[300px] overflow-y-scroll"
            valueStyle={{ width: "220px" }}
            options={agents || []}
            value={selectedAgent?.value}
            onSelect={onSelectAgent}
          />
        </div>
      </div>
    </>
  );
};

const AssignmentSuccess = ({ data }) => {
  if (!data) return null;

  const acceptedByName = data.acceptedByName || "Admin";
  const assignedTime = data.assignedAgentAt || data.updatedAt;
  const formattedTime = formatDateTimeWithToday(assignedTime);

  // Map state logic to display text
  let statusText = "Accepted";
  let descriptionText = "Trike agent is assigned for this request";
  if (data.status === 4) {
    statusText = "In Progress";
    descriptionText = "Technical Agent is currently repairing the trike";
  } else if (data.status === 5) {
    statusText = "Completed";
    descriptionText = "Trike repair completed successfully";
  }

  const arrivalTime = data.agentArrivedAt || data.updatedAt;
  const arrivalTimeFormatted = formatDateTimeWithToday(arrivalTime);

  return (
    <>
      <RequestDetails data={data} />

      <div className="mt-3 bg-primary-10 rounded-xl py-4 px-6">
        <div
          className="
            inline-flex
            items-center
            px-2
            py-1
            rounded-md
            border
            border-primary
            bg-white
          "
        >
          <span className="font-poppins font-medium text-xs text-primary leading-none">
            {statusText}
          </span>
        </div>

        {data.status === 6 ? (
          <div className="flex items-start gap-2 my-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mt-0.5 flex-shrink-0"
            >
              <path
                d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                stroke="#C84630"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 6V11"
                stroke="#C84630"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 14H10.01"
                stroke="#C84630"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="font-poppins font-medium text-sm text-[#C84630] leading-snug">
              The trike couldn’t be repaired on ground, requires service.
            </p>
          </div>
        ) : (
          <p className="mt-4 font-poppins font-medium text-sm text-black leading-none ">
            {descriptionText}
          </p>
        )}

        <div className="mt-3 flex">
          <LabelAndValue value={acceptedByName} label="Accepted By" />
          <LabelAndValue value={formattedTime} label="Date & Time" />
        </div>

        <div className="mt-4 space-y-3">
          <div
            className="
              bg-white
              rounded-md
              p-4
              flex
              items-center
              justify-between
            "
          >
            <div className="flex items-center gap-2">
              <div
                className="
                  w-10
                  h-10
                  rounded-full
                  bg-primary-10
                  flex
                  items-center
                  justify-center
                "
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.10573 7.24634C8.03906 7.23967 7.95906 7.23967 7.88573 7.24634C6.29906 7.19301 5.03906 5.89301 5.03906 4.29301C5.03906 2.65967 6.35906 1.33301 7.99906 1.33301C9.6324 1.33301 10.9591 2.65967 10.9591 4.29301C10.9524 5.89301 9.6924 7.19301 8.10573 7.24634Z"
                    stroke="#085054"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.7725 9.70699C3.15917 10.787 3.15917 12.547 4.7725 13.6203C6.60583 14.847 9.6125 14.847 11.4458 13.6203C13.0592 12.5403 13.0592 10.7803 11.4458 9.70699C9.61917 8.48699 6.6125 8.48699 4.7725 9.70699Z"
                    stroke="#085054"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <LabelAndValue
                value={data.agentName || data.agent_name || "Technical Agent"}
                label={
                  data.agentArrived || data.agent_arrived
                    ? `Arrival time: ${arrivalTimeFormatted}`
                    : "On the way"
                }
              />
            </div>

            <div
              className="
                bg-primary-10
                rounded-md
                px-2
                py-1
                flex
                items-center
                gap-2
              "
            >
              <span className="font-poppins font-medium text-xs leading-none text-primary">
                {data.status === 5 || data.status === 6
                  ? "Completed"
                  : "Assigned"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const LabelAndValue = ({ label = "", value = "", shouldCapitalize = true }) => {
  return (
    <div className="flex-1 font-poppins">
      <p
        className="font-medium text-sm text-black"
        style={{ textTransform: shouldCapitalize && "capitalize" }}
      >
        {value || "--"}
      </p>

      <p className="mt-1 font-normal text-xs text-dark-gray">{label}</p>
    </div>
  );
};

export default TrikeRepairDrawer;
